import { Head, router } from "@inertiajs/react"
import { useState } from "react"

import {
  HpiDataTable,
  HpiFilterSelect,
  HpiPagination,
  HpiStatsBar,
} from "@/components/hpi"
import type { HpiColumn } from "@/components/hpi"
import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

/* -- Types ------------------------------------------------ */

interface MailingListRow {
  row_key: string
  customer_id: number
  contact_id: number | null
  company: string | null
  first_name: string | null
  last_name: string | null
  contact_name: string | null
  title: string | null
  address: string | null
  address2: string | null
  city: string | null
  state: string | null
  zip: string | null
  email: string | null
}

interface SelectOption {
  value: string | number
  label: string
}

interface Props {
  results: MailingListRow[]
  show_contacts: boolean
  view: string
  total_count: number
  client_types: SelectOption[]
  categories: SelectOption[]
  states: SelectOption[]
  selected_type_id: string
  selected_category_id: string
  selected_contact_cat: boolean
  selected_state: string
  selected_group: string
  selected_view: string
}

/* -- Columns ---------------------------------------------- */

const PER_PAGE = 25

function previewColumns(showContacts: boolean): HpiColumn<MailingListRow>[] {
  const cols: HpiColumn<MailingListRow>[] = [
    {
      header: "Firm",
      render: (row) => (
        <a
          href={`/customers/${row.customer_id}`}
          target="_blank"
          rel="noreferrer"
          className="text-sm text-blue-600 hover:underline"
        >
          {row.company ?? "-"}
        </a>
      ),
    },
  ]

  if (showContacts) {
    cols.push(
      {
        header: "Contact",
        render: (row) =>
          row.contact_id ? (
            <a
              href={`/customers/${row.customer_id}/contacts/${row.contact_id}/edit`}
              target="_blank"
              rel="noreferrer"
              className="text-sm text-blue-600 hover:underline"
            >
              {row.contact_name ?? "-"}
            </a>
          ) : (
            <span className="text-sm">-</span>
          ),
      },
      {
        header: "Title",
        width: "max-w-[140px]",
        render: (row) => <span className="text-sm">{row.title ?? ""}</span>,
      },
    )
  }

  cols.push(
    {
      header: "Address",
      render: (row) => <span className="text-sm">{row.address ?? ""}</span>,
    },
    {
      header: "Suite/Building",
      width: "max-w-[140px]",
      render: (row) => <span className="text-sm">{row.address2 ?? ""}</span>,
    },
    {
      header: "City",
      width: "max-w-[120px]",
      render: (row) => <span className="text-sm">{row.city ?? ""}</span>,
    },
    {
      header: "State",
      width: "max-w-[60px]",
      render: (row) => <span className="text-sm">{row.state ?? ""}</span>,
    },
    {
      header: "Zip",
      width: "max-w-[80px]",
      render: (row) => <span className="text-sm">{row.zip ?? ""}</span>,
    },
    {
      header: "Email",
      render: (row) => <span className="text-sm">{row.email ?? ""}</span>,
    },
  )

  return cols
}

function emailColumns(showContacts: boolean): HpiColumn<MailingListRow>[] {
  const cols: HpiColumn<MailingListRow>[] = []

  if (showContacts) {
    cols.push(
      {
        header: "First",
        render: (row) => (
          <span className="text-sm">{row.first_name ?? ""}</span>
        ),
      },
      {
        header: "Last",
        render: (row) => <span className="text-sm">{row.last_name ?? ""}</span>,
      },
    )
  }

  cols.push(
    {
      header: "Firm",
      render: (row) => <span className="text-sm">{row.company ?? ""}</span>,
    },
    {
      header: "Email",
      render: (row) => <span className="text-sm">{row.email ?? ""}</span>,
    },
  )

  return cols
}

function mailingColumns(showContacts: boolean): HpiColumn<MailingListRow>[] {
  const cols: HpiColumn<MailingListRow>[] = [
    {
      header: "Firm",
      render: (row) => <span className="text-sm">{row.company ?? ""}</span>,
    },
  ]

  if (showContacts) {
    cols.push({
      header: "Contact",
      render: (row) => (
        <span className="text-sm">{row.contact_name ?? ""}</span>
      ),
    })
  }

  cols.push(
    {
      header: "Address",
      render: (row) => <span className="text-sm">{row.address ?? ""}</span>,
    },
    {
      header: "Suite/Building",
      width: "max-w-[140px]",
      render: (row) => <span className="text-sm">{row.address2 ?? ""}</span>,
    },
    {
      header: "City",
      width: "max-w-[120px]",
      render: (row) => <span className="text-sm">{row.city ?? ""}</span>,
    },
    {
      header: "State",
      width: "max-w-[60px]",
      render: (row) => <span className="text-sm">{row.state ?? ""}</span>,
    },
    {
      header: "Zip",
      width: "max-w-[80px]",
      render: (row) => <span className="text-sm">{row.zip ?? ""}</span>,
    },
  )

  return cols
}

/* -- Main Component --------------------------------------- */

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Reports", href: "/reports/case_activity" },
  { title: "Mailing Lists", href: "/reports/marketing/mailing_lists" },
]

const groupOptions = [
  { value: "Contacts", label: "All Contacts" },
  { value: "Company", label: "Company" },
]

const viewOptions = [
  { value: "Preview", label: "Preview" },
  { value: "Email", label: "Email" },
  { value: "Mailing", label: "Mailing" },
]

export default function MailingListsReport({
  results,
  show_contacts,
  view,
  total_count,
  client_types,
  categories,
  states,
  selected_type_id,
  selected_category_id,
  selected_contact_cat,
  selected_state,
  selected_group,
  selected_view,
}: Props) {
  const [typeId, setTypeId] = useState(selected_type_id)
  const [categoryId, setCategoryId] = useState(selected_category_id)
  const [contactCat, setContactCat] = useState(selected_contact_cat)
  const [stateFilter, setStateFilter] = useState(selected_state)
  const [group, setGroup] = useState(selected_group)
  const [viewMode, setViewMode] = useState(selected_view)
  const [page, setPage] = useState(1)

  const totalPages = Math.ceil(results.length / PER_PAGE)
  const paginated = results.slice((page - 1) * PER_PAGE, page * PER_PAGE)

  const columns =
    view === "Email"
      ? emailColumns(show_contacts)
      : view === "Mailing"
        ? mailingColumns(show_contacts)
        : previewColumns(show_contacts)

  const handleUpdate = () => {
    setPage(1)
    router.get(
      "/reports/marketing/mailing_lists",
      {
        type_id: typeId || undefined,
        category_id: categoryId || undefined,
        contact_cat: contactCat ? "true" : undefined,
        state: stateFilter || undefined,
        group: group,
        view: viewMode,
      },
      { preserveState: true },
    )
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Mailing Lists" />

      <div className="p-6">
        {/* Filter Bar */}
        <div className="bg-hpi-pale-gray mb-6 flex flex-wrap items-end gap-4 rounded-lg px-4 py-3">
          <HpiFilterSelect
            label="Type"
            value={typeId}
            onChange={setTypeId}
            options={client_types}
          />
          <div>
            <HpiFilterSelect
              label="Category"
              value={categoryId}
              onChange={setCategoryId}
              options={categories}
            />
            <label className="mt-1 flex items-center gap-1.5 text-xs text-gray-600">
              <input
                type="checkbox"
                checked={contactCat}
                onChange={(e) => setContactCat(e.target.checked)}
                className="rounded border-gray-300"
              />
              Apply to Contacts
            </label>
          </div>
          <HpiFilterSelect
            label="State"
            value={stateFilter}
            onChange={setStateFilter}
            options={states}
          />
          <HpiFilterSelect
            label="Select"
            value={group}
            onChange={setGroup}
            options={groupOptions}
            placeholder=""
          />
          <HpiFilterSelect
            label="View"
            value={viewMode}
            onChange={setViewMode}
            options={viewOptions}
            placeholder=""
          />
          <button
            onClick={handleUpdate}
            className="border-hpi-dark-blue text-hpi-dark-blue hover:bg-hpi-dark-blue h-8 rounded border px-5 text-sm font-medium transition-colors hover:text-white"
          >
            Search
          </button>
        </div>

        {/* Data Table */}
        <HpiDataTable<MailingListRow>
          title={`Mailing List — ${view} View`}
          columns={columns}
          data={paginated}
          keyFn={(row) => row.row_key}
          pagination={
            results.length > PER_PAGE ? (
              <HpiPagination
                currentPage={page}
                totalPages={totalPages}
                totalItems={results.length}
                perPage={PER_PAGE}
                onPageChange={setPage}
              />
            ) : undefined
          }
        />

        {/* Summary Stats */}
        <HpiStatsBar
          className="mt-6"
          stats={[
            {
              label: "Total",
              subLabel: "Records",
              value: total_count,
            },
          ]}
        />
      </div>
    </AppLayout>
  )
}
